// src/components/SuperAdminUsersMobile.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Stack,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import { collection, doc, getDoc, getDocs, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import MobileHeader from "./headers/MobileHearder";

const MONTHLY_PRICE_USD = 15;

export default function SuperAdminUsersMobile() {
  const { currentUser } = useAuth();
  const { translate } = useContext(LanguageContext);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [tenantTokens, setTenantTokens] = useState({});
  const [loadingTokens, setLoadingTokens] = useState(false);
  const [tenantPlans, setTenantPlans] = useState({});

  useEffect(() => {
    if (!currentUser) {
      setIsSuperAdmin(false);
      setCheckingRole(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, "profilsUsers", currentUser.uid),
      (snap) => {
        if (snap.exists()) {
          setIsSuperAdmin(snap.data()?.role === "SuperAdmin");
        } else {
          setIsSuperAdmin(false);
        }
        setCheckingRole(false);
      }
    );
    return () => unsub();
  }, [currentUser]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    setLoadingProfiles(true);
    getDocs(collection(db, "profilsUsers"))
      .then((snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setProfiles(list);
      })
      .finally(() => setLoadingProfiles(false));
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin || profiles.length === 0) {
      setTenantTokens({});
      return;
    }
    const uniqueTenantIds = Array.from(
      new Set(
        profiles
          .map((p) => p.tenantId)
          .filter((t) => typeof t === "string" && t.trim() !== "")
      )
    );
    if (uniqueTenantIds.length === 0) {
      setTenantTokens({});
      return;
    }
    setLoadingTokens(true);
    Promise.all(
      uniqueTenantIds.map(async (tenantId) => {
        try {
          const tenantDocSnap = await getDoc(doc(db, "tenants", tenantId));
          const plan = tenantDocSnap.exists()
            ? tenantDocSnap.data()?.plan || "unknown"
            : "unknown";

          const agentsSnap = await getDocs(
            collection(db, "tenants", tenantId, "agents")
          );
          let total = 0;
          for (const agentDoc of agentsSnap.docs) {
            const usageSnap = await getDocs(
              collection(
                db,
                "tenants",
                tenantId,
                "agents",
                agentDoc.id,
                "usage_daily"
              )
            );
            usageSnap.forEach((d) => {
              total += Number(d.data()?.totalTokens || 0) || 0;
            });
          }
          return { tenantId, total, plan };
        } catch (e) {
          console.error("Failed to load tokens for tenant", tenantId, e);
          return { tenantId, total: 0, plan: "unknown" };
        }
      })
    )
      .then((arr) => {
        const mapTokens = {};
        const mapPlans = {};
        arr.forEach(({ tenantId, total, plan }) => {
          mapTokens[tenantId] = total;
          mapPlans[tenantId] = plan;
        });
        setTenantTokens(mapTokens);
        setTenantPlans(mapPlans);
      })
      .finally(() => setLoadingTokens(false));
  }, [isSuperAdmin, profiles]);

  const rows = useMemo(() => {
    return profiles.map((p) => ({
      id: p.id,
      name: p.name || "N/A",
      email: p.email || "N/A",
      role: p.role || "Normal User",
      plan:
        p.plan ||
        (p.tenantId ? tenantPlans[p.tenantId] || "unknown" : "none"),
      tokens: p.tenantId ? tenantTokens[p.tenantId] || 0 : 0,
    }));
  }, [profiles, tenantTokens, tenantPlans]);

  const globalTotalTokens = useMemo(() => {
    const seen = new Set();
    let total = 0;
    Object.entries(tenantTokens).forEach(([tid, value]) => {
      if (seen.has(tid)) return;
      seen.add(tid);
      total += value || 0;
    });
    return total;
  }, [tenantTokens]);

  const counts = useMemo(() => {
    const premium = rows.filter((r) => r.plan === "premium").length;
    const free = rows.filter((r) => r.plan === "free" || r.plan === "none").length;
    const other = Math.max(rows.length - premium - free, 0);
    return { premium, free, other };
  }, [rows]);

  const monthlyRevenue = counts.premium * MONTHLY_PRICE_USD;

  if (checkingRole || !currentUser) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isSuperAdmin) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          color: "text.primary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
        }}
      >
        <Typography variant="h6">
          {translate("AccessDenied") || "Access restricted to SuperAdmin users."}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      <MobileHeader title={translate("SuperAdminTitle") || "Super Admin"} />
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={700} gutterBottom>
                  {translate("TotalTokensAllTenants") ||
                    "Total tokens (all tenants)"}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  {globalTotalTokens.toLocaleString()}
                </Typography>
                {(loadingProfiles || loadingTokens) && (
                  <CircularProgress size={20} sx={{ mt: 1 }} />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography fontWeight={700} gutterBottom>
                  {translate("MonthlyRevenueEst") || "Monthly revenue (est.)"}
                </Typography>
                <Typography variant="h5" fontWeight={800}>
                  ${monthlyRevenue.toLocaleString()} / mo
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(translate("MonthlyRevenueHint") ||
                    "Based on {count} premium accounts at $15/mo.").replace(
                    "{count}",
                    counts.premium
                  )}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip label={`Premium: ${counts.premium}`} color="primary" />
              <Chip label={`Free: ${counts.free}`} color="success" />
              <Chip label={`Other: ${counts.other}`} color="default" />
            </Stack>
            <Box
              sx={{
                width: 200,
                height: 200,
                mx: "auto",
                mb: 2,
                borderRadius: "50%",
                background: `conic-gradient(#4f46e5 0deg ${
                  counts.premium + counts.free + counts.other === 0
                    ? 0
                    : (counts.premium /
                        (counts.premium + counts.free + counts.other)) *
                      360
                }deg, #10b981 ${
                  counts.premium + counts.free + counts.other === 0
                    ? 0
                    : (counts.premium /
                        (counts.premium + counts.free + counts.other)) *
                      360
                }deg ${
                  counts.premium + counts.free + counts.other === 0
                    ? 0
                    : ((counts.premium + counts.free) /
                        (counts.premium + counts.free + counts.other)) *
                      360
                }deg, #9ca3af ${
                  counts.premium + counts.free + counts.other === 0
                    ? 0
                    : ((counts.premium + counts.free) /
                        (counts.premium + counts.free + counts.other)) *
                      360
                }deg 360deg)`,
                boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: 110,
                  height: 110,
                  borderRadius: "50%",
                  backgroundColor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06)",
                }}
              >
                <Typography variant="h6" fontWeight={800}>
                  {counts.premium + counts.free + counts.other}
                </Typography>
              </Box>
            </Box>
            <List dense>
              {rows.map((r) => (
                <ListItem
                  key={r.id}
                  alignItems="flex-start"
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <ListItemText
                    primary={
                      <Typography fontWeight={700}>{r.name}</Typography>
                    }
                    secondary={
                      <>
                        <Typography variant="body2" color="text.secondary">
                          {r.email}
                        </Typography>
                        <Typography variant="body2">
                          {translate("Role") || "Role"}: {r.role} —{" "}
                          {translate("Plan") || "Plan"}:{" "}
                          {r.plan === "free"
                            ? "Free"
                            : r.plan === "premium"
                            ? "Premium"
                            : r.plan === "none"
                            ? translate("PlanNoneLabel") || "No tenant"
                            : translate("PlanUnknownLabel") || "Unknown"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {translate("Tokens") || "Tokens"}:{" "}
                          {r.tokens.toLocaleString()}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
              {!rows.length && (
                <ListItem>
                  <ListItemText
                    primary={
                      loadingProfiles
                        ? translate("LoadingUsers") || "Loading users..."
                        : translate("NoUsersFound") || "No users found"
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}
